import logger from '@eventbrite/client-logger';
import {
    client as braintreeClient,
    GooglePayment,
    googlePayment,
} from 'braintree-web';
import { Dispatch } from 'redux';
import { GOOGLE_PAY_MERCHANT_ID } from '../constants/googlePay';
import { getTotalAmount } from '../selectors/order';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';
import { OrderNamespace } from '../state/order';
import { getAndSetBraintreeToken } from './order/common';

export enum GooglePayActionType {
    setGooglePayPaymentRequest = 'setGooglePayPaymentRequest',
    setGooglePayInstance = 'setGooglePayInstance',
    setIsLoadingGooglePay = 'setIsLoadingGooglePay',
}

export interface SetGooglePayInstanceAction {
    type: GooglePayActionType.setGooglePayInstance;
    payload: {
        googlePayInstance: GooglePayment;
    };
}

export interface SetIsLoadingGooglePayAction {
    type: GooglePayActionType.setIsLoadingGooglePay;
    payload: {
        isLoadingGooglePay: boolean;
    };
}

export interface SetGooglePayPaymentRequestAction {
    type: GooglePayActionType.setGooglePayPaymentRequest;
    payload: {
        googlePayPaymentRequest: google.payments.api.PaymentDataRequest;
    };
}

export const setGooglePayInstance = (
    googlePayInstance: GooglePayment,
): SetGooglePayInstanceAction => ({
    type: GooglePayActionType.setGooglePayInstance,
    payload: {
        googlePayInstance,
    },
});

export const setIsLoadingGooglePay = (
    isLoadingGooglePay: boolean,
): SetIsLoadingGooglePayAction => ({
    type: GooglePayActionType.setIsLoadingGooglePay,
    payload: {
        isLoadingGooglePay,
    },
});

export const setGooglePayPaymentRequest = (
    googlePayPaymentRequest: google.payments.api.PaymentDataRequest,
): SetGooglePayPaymentRequestAction => ({
    type: GooglePayActionType.setGooglePayPaymentRequest,
    payload: {
        googlePayPaymentRequest,
    },
});

const getGooglePayPaymentRequest = async (
    state: CheckoutState,
    googlePayInstance: GooglePayment,
): Promise<google.payments.api.PaymentDataRequest> => {
    const { event } = state as {
        event: EventNamespace;
    };

    const payRequest = {
        merchantInfo: {
            merchantId: GOOGLE_PAY_MERCHANT_ID,
        },
        transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: getTotalAmount(state).toString(),
            currencyCode: event.currency || 'USD',
        },
    };

    return googlePayInstance.createPaymentDataRequest(payRequest);
};

const getGooglePayInstance = async (order: OrderNamespace) => {
    const braintreeToken = order.braintreeClientToken;
    const client = await braintreeClient.create({
        authorization: braintreeToken,
    });

    return googlePayment.create({
        client,
        googlePayVersion: 2,
        googleMerchantId: GOOGLE_PAY_MERCHANT_ID,
    });
};

export const onGooglePayMethodSelected = () => async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    dispatch(setIsLoadingGooglePay(true));

    try {
        await dispatch(getAndSetBraintreeToken());

        const state = getState();
        const googlePayInstance = await getGooglePayInstance(
            state.order as OrderNamespace,
        );
        const googlePayPaymentRequest = await getGooglePayPaymentRequest(
            state,
            googlePayInstance,
        );

        dispatch(setGooglePayInstance(googlePayInstance));
        dispatch(setGooglePayPaymentRequest(googlePayPaymentRequest));
    } catch (error) {
        logger.error(error);
    } finally {
        dispatch(setIsLoadingGooglePay(false));
    }
};

export type GooglePayAction =
    | SetGooglePayInstanceAction
    | SetGooglePayPaymentRequestAction
    | SetIsLoadingGooglePayAction;
